import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Search, Loader2, Send, Link2, Copy, Ban, Trash2,
  User, Plus, X, Mail, MessageSquare, CheckCircle2, Download, FileCheck2,
} from "lucide-react";

const STATUS_META = {
  BOZZA: { label: "Bozza", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  IN_ATTESA: { label: "In attesa di conferma", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  CONFERMATO: { label: "Confermato", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  SCADUTO: { label: "Scaduto", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  ANNULLATO: { label: "Annullato", cls: "bg-red-100 text-red-700 border-red-200" },
};

const DEFAULT_ITEMS = [
  { key: "privacy", label: "Informativa privacy — presa visione e accettazione", required: true },
  { key: "marketing", label: "Consenso al trattamento dei dati per finalità di marketing", required: false },
  { key: "comunicazioni", label: "Consenso a ricevere comunicazioni elettroniche (email/SMS)", required: false },
];

const copyText = async (t) => {
  try { await navigator.clipboard.writeText(t); return true; } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
      return true;
    } catch { return false; }
  }
};

export default function SignConsensi() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [idQuery, setIdQuery] = useState("");
  const [idResults, setIdResults] = useState([]);
  const [title, setTitle] = useState("");
  const [introText, setIntroText] = useState("");
  const [consentItems, setConsentItems] = useState(DEFAULT_ITEMS);

  const [linkDialog, setLinkDialog] = useState(null); // { url, id }
  const [notifySms, setNotifySms] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (status = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/sign/consents", { params: { status } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(statusFilter); }, [load, statusFilter]);

  const displayIdentity = (it) =>
    [it.nome, it.cognome].filter(Boolean).join(" ") || it.ragione_sociale || it.identity_id;

  const searchIdentity = async () => {
    if (!idQuery.trim()) return;
    try {
      const { data } = await api.get("/sign/identities", { params: { q: idQuery } });
      setIdResults(data);
      if (!data.length) toast.info("Nessun cliente trovato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCreate = () => {
    setIdentity(null); setIdQuery(""); setIdResults([]);
    setTitle(""); setIntroText(""); setConsentItems(DEFAULT_ITEMS);
    setDialogOpen(true);
  };

  const toggleItem = (key, required) => {
    setConsentItems((prev) => prev.map((i) => i.key === key ? { ...i, required } : i));
  };
  const removeItem = (key) => setConsentItems((prev) => prev.filter((i) => i.key !== key));
  const addItem = () => setConsentItems((prev) => [...prev, { key: `voce_${Date.now()}`, label: "", required: false }]);
  const editItemLabel = (key, label) => setConsentItems((prev) => prev.map((i) => i.key === key ? { ...i, label } : i));

  const create = async () => {
    if (!identity) { toast.error("Seleziona un cliente"); return; }
    if (!title.trim()) { toast.error("Inserisci un titolo"); return; }
    const validItems = consentItems.filter((i) => i.label.trim());
    if (!validItems.length) { toast.error("Almeno una voce di consenso"); return; }
    setCreating(true);
    try {
      await api.post("/sign/consents", {
        identity_id: identity.identity_id, title: title.trim(), intro_text: introText.trim(),
        items: validItems.map((i) => ({ key: i.key, label: i.label.trim(), required: !!i.required })),
      });
      toast.success("Richiesta di consenso creata (bozza)");
      setDialogOpen(false);
      load(statusFilter);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const buildUrl = (token) => `${window.location.origin}/consenso/${token}`;

  const activate = async (c) => {
    try {
      const { data } = await api.post(`/sign/consents/${c.consent_id}/send`, { base_url: window.location.origin });
      const url = buildUrl(data.token);
      await copyText(url);
      setLinkDialog({ url, id: c.consent_id });
      toast.success("Link generato e copiato");
      load(statusFilter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyLink = async (c) => {
    try {
      const { data } = await api.get(`/sign/consents/${c.consent_id}`);
      if (!data.token) { toast.error("Link non disponibile"); return; }
      const url = buildUrl(data.token);
      const ok = await copyText(url);
      setLinkDialog({ url, id: c.consent_id });
      if (ok) toast.success("Link copiato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const notifyClient = async () => {
    if (!linkDialog?.id) return;
    const channels = [];
    if (notifySms) channels.push("sms");
    if (notifyEmail) channels.push("email");
    if (!channels.length) { toast.error("Seleziona almeno un canale"); return; }
    setNotifying(true);
    try {
      const { data } = await api.post(`/sign/consents/${linkDialog.id}/notify`, { channels, base_url: window.location.origin });
      const r = data.result || {};
      const msgs = []; let anyFail = false;
      if (r.sms) { msgs.push(r.sms.sent ? `SMS inviato (${r.sms.to})` : `SMS non inviato: ${r.sms.reason || "errore"}`); anyFail = anyFail || !r.sms.sent; }
      if (r.email) { msgs.push(r.email.sent ? `Email inviata (${r.email.to})` : `Email non inviata: ${r.email.reason || "errore"}`); anyFail = anyFail || !r.email.sent; }
      (anyFail ? toast.warning : toast.success)(msgs.join(" · ") || "Inviato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setNotifying(false); }
  };

  const downloadReceipt = async (c) => {
    try {
      const { data } = await api.get(`/sign/consents/${c.consent_id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ricevuta_consenso_${c.consent_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Ricevuta non disponibile"); }
  };

  const doCancel = async () => {
    if (!cancelTarget) return;
    try {
      await api.post(`/sign/consents/${cancelTarget.consent_id}/cancel`);
      toast.success("Richiesta annullata"); setCancelTarget(null); load(statusFilter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sign/consents/${deleteTarget.consent_id}`);
      toast.success("Richiesta eliminata"); setDeleteTarget(null); load(statusFilter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-consensi-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => nav("/assietica-sign")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="sign-consensi-back-btn">
          <ArrowLeft className="h-4 w-4" /> Assietica Sign
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <FileCheck2 className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">Consensi (multicanale)</h1>
              <p className="text-sm text-slate-500">Raccogli i consensi del cliente via SMS/email con conferma tramite codice OTP</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-consenso-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova richiesta
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button onClick={() => setStatusFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${!statusFilter ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`} data-testid="sign-consenso-filter-all">Tutte</button>
          {Object.keys(STATUS_META).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`sign-consenso-filter-${s}`}>
              {STATUS_META[s].label}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="sign-consenso-empty">Nessuna richiesta di consenso. Creane una con "Nuova richiesta".</div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="sign-consenso-list">
              {items.map((c) => {
                const sm = STATUS_META[c.status] || STATUS_META.BOZZA;
                return (
                  <div key={c.consent_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50/60" data-testid={`sign-consenso-row-${c.consent_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-slate-400">{c.consent_id}</span>
                        <Badge variant="outline" className={sm.cls} data-testid={`sign-consenso-status-${c.consent_id}`}>{sm.label}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-700 flex-wrap">
                        <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-slate-400" /> {c.identity_name}</span>
                        <span className="text-slate-800 font-medium">{c.title}</span>
                        <span className="text-xs text-slate-400">{(c.items || []).length} voci</span>
                      </div>
                      {c.status === "CONFERMATO" && c.confirmation && (
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-emerald-700">
                          <CheckCircle2 className="h-3 w-3 shrink-0" /> Confermato da {c.confirmation.full_name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {c.status === "BOZZA" && (
                        <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => activate(c)} data-testid={`sign-consenso-activate-${c.consent_id}`}>
                          <Send className="h-4 w-4 mr-1" /> Attiva link
                        </Button>
                      )}
                      {c.status === "IN_ATTESA" && (
                        <Button size="sm" variant="outline" onClick={() => copyLink(c)} data-testid={`sign-consenso-copylink-${c.consent_id}`}>
                          <Link2 className="h-4 w-4 mr-1" /> Copia link
                        </Button>
                      )}
                      {c.status === "CONFERMATO" && (
                        <Button size="sm" variant="outline" onClick={() => downloadReceipt(c)} data-testid={`sign-consenso-receipt-${c.consent_id}`}>
                          <Download className="h-4 w-4 mr-1" /> Ricevuta
                        </Button>
                      )}
                      {(c.status === "BOZZA" || c.status === "IN_ATTESA") && (
                        <Button size="sm" variant="ghost" className="text-slate-500 hover:text-orange-600" onClick={() => setCancelTarget(c)} data-testid={`sign-consenso-cancel-${c.consent_id}`}>
                          <Ban className="h-4 w-4 mr-1" /> Annulla
                        </Button>
                      )}
                      {c.status !== "CONFERMATO" && (
                        <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-500 hover:text-red-600" onClick={() => setDeleteTarget(c)} data-testid={`sign-consenso-delete-${c.consent_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-4 text-xs text-slate-500" data-testid="sign-consenso-note">
          Strumento leggero interno: registra il consenso e le evidenze tecniche (nome, esito, data/ora, IP, OTP verificato).
          Non costituisce Firma Elettronica Avanzata/Qualificata né conservazione a norma.
        </div>
      </main>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sign-consenso-dialog">
          <DialogHeader>
            <DialogTitle>Nuova richiesta di consenso</DialogTitle>
            <DialogDescription>Seleziona il cliente, imposta il testo e le voci di consenso. Il cliente confermerà con un codice OTP.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <Label className="text-xs text-slate-500 mb-1.5 block">Cliente (Identity)</Label>
              {identity ? (
                <div className="inline-flex items-center gap-1.5 text-sm text-slate-800">
                  <User className="h-4 w-4 text-sky-600" /> {displayIdentity(identity)} <span className="text-xs text-slate-400">({identity.identity_id})</span>
                  <button onClick={() => setIdentity(null)} className="text-slate-400 hover:text-red-600 ml-1"><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Input value={idQuery} onChange={(e) => setIdQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchIdentity())} placeholder="Cerca cliente…" className="h-9" data-testid="sign-consenso-identity-search" />
                    <Button type="button" variant="outline" className="h-9" onClick={searchIdentity} data-testid="sign-consenso-identity-search-btn"><Search className="h-4 w-4" /></Button>
                  </div>
                  {idResults.length > 0 && (
                    <div className="mt-2 max-h-36 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                      {idResults.map((it) => (
                        <button key={it.identity_id} type="button" onClick={() => { setIdentity(it); setIdResults([]); setIdQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm" data-testid={`sign-consenso-identity-result-${it.identity_id}`}>
                          <div className="font-medium text-slate-800">{displayIdentity(it)}</div>
                          <div className="text-xs text-slate-400">{[it.codice_fiscale, it.email, it.cellulare].filter(Boolean).join(" · ")}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Titolo</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Consensi privacy — Polizza RC" data-testid="sign-consenso-title" />
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Testo informativa (opzionale)</Label>
              <Textarea value={introText} onChange={(e) => setIntroText(e.target.value)} rows={3} placeholder="Testo mostrato al cliente prima della conferma…" data-testid="sign-consenso-intro" />
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-slate-500">Voci di consenso</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addItem} data-testid="sign-consenso-add-item"><Plus className="h-3.5 w-3.5 mr-1" /> Voce</Button>
              </div>
              <div className="space-y-2">
                {consentItems.map((it) => (
                  <div key={it.key} className="flex items-center gap-2" data-testid={`sign-consenso-item-${it.key}`}>
                    <Input value={it.label} onChange={(e) => editItemLabel(it.key, e.target.value)} placeholder="Descrizione voce" className="h-8 text-sm flex-1" />
                    <label className="flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap cursor-pointer">
                      <Checkbox checked={!!it.required} onCheckedChange={(v) => toggleItem(it.key, !!v)} /> obblig.
                    </label>
                    <button type="button" onClick={() => removeItem(it.key)} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Le voci "obbligatorie" devono essere accettate dal cliente per poter confermare.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sign-consenso-cancel-create">Annulla</Button>
            <Button onClick={create} disabled={creating} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-consenso-create">
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Crea bozza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="sign-consenso-link-dialog">
          <DialogHeader>
            <DialogTitle>Link di conferma consensi</DialogTitle>
            <DialogDescription>Condividi il link con il cliente oppure invialo direttamente via SMS/email.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={linkDialog?.url || ""} className="font-mono text-xs" data-testid="sign-consenso-link-value" />
            <Button variant="outline" onClick={async () => { const ok = await copyText(linkDialog.url); if (ok) toast.success("Link copiato"); }} data-testid="sign-consenso-link-copy">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="rounded-lg border border-slate-200 p-3 mt-2">
            <p className="text-xs text-slate-500 mb-2">Invia il link direttamente al cliente:</p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={notifySms} onCheckedChange={(v) => setNotifySms(!!v)} data-testid="sign-consenso-notify-sms" />
                <MessageSquare className="h-4 w-4 text-slate-400" /> Via SMS (al cellulare del cliente)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={notifyEmail} onCheckedChange={(v) => setNotifyEmail(!!v)} data-testid="sign-consenso-notify-email" />
                <Mail className="h-4 w-4 text-slate-400" /> Via email (all'indirizzo del cliente)
              </label>
            </div>
            <Button onClick={notifyClient} disabled={notifying} className="w-full mt-3 bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-consenso-notify-btn">
              {notifying ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />} Invia al cliente
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setLinkDialog(null)} data-testid="sign-consenso-link-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="sign-consenso-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare la richiesta?</AlertDialogTitle>
            <AlertDialogDescription>{cancelTarget && <>La richiesta <strong>{cancelTarget.consent_id}</strong> verrà annullata e il link non sarà più valido.</>}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-consenso-cancel-no">No</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} className="bg-orange-600 hover:bg-orange-700" data-testid="sign-consenso-cancel-yes">Annulla richiesta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="sign-consenso-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la richiesta?</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget && <>La richiesta <strong>{deleteTarget.consent_id}</strong> verrà eliminata definitivamente.</>}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-consenso-delete-no">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="sign-consenso-delete-yes">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
